import React, { FunctionComponent, useEffect, useState } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import cuid from 'cuid';
import { History, Location } from 'history';
import Loader from '@empiriecom/module-components/Loader';
import useIconSet from '@empiriecom/module-components/utils/getIconSet';
import { parse } from 'query-string';
import useConfig from '@empiriecom/module-inspire-components/components/hooks/useConfig';
import { FontSizes } from '@empiriecom/module-inspire-components/utils/style.drupal';
import { Languages, FragmentConfig } from '@empiriecom/module-inspire-components/interfaces/types';
import { DataGlobal } from '@empiriecom/module-inspire-components/interfaces/cmsdata';
import timeoutCapableFetch from '@empiriecom/module-inspire-components/utils/timeoutCapableFetch';
import userPreferredLocale from '@empiriecom/module-inspire-components/utils/userPreferredLocale';
import { Esi } from '@empiriecom/module-components/Esi';
import {
  getCmsApiUrl,
  getFallbackApiUrl,
  getPath,
} from '@empiriecom/module-inspire-components/utils/apiUrl';
import { trackingProvider } from '@empiriecom/module-inspire-components/providers/trackingProvider';
import { FailureData } from '@empiriecom/module-inspire-components/mockdata/cmsdata';
import ComponentRecognizer from '@empiriecom/module-inspire-components/components/ComponentRecognizer';
import HistoryContext from '@empiriecom/module-inspire-components/providers/historyProvider';
import CustomNavigation from '@empiriecom/module-inspire-components/components/CustomNavigation';
import Meta from '@empiriecom/module-inspire-components/components/Meta';
import NewsletterSheet from '@empiriecom/module-inspire-components/components/NewsletterSheet';
import { CompanyId } from '@empiriecom/module-components/utils/interfaces';

const StyledPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center; /* horizontal alignment */
  align-items: center; /* vertical alignment */
  text-align: left;
  ${FontSizes()}
`;

const GlobalStyle = createGlobalStyle`
  .page-container .distance-variation-1 {
    padding: 0 1.5rem;

    @media only screen and (min-width: ${({ desktopMinWidth }: { desktopMinWidth: string }) =>
      desktopMinWidth}) {
      padding-left: 0;
      padding-right: 0;
    }
  }

  .page-container .distance-fullwidth {
    max-width: unset;

    @media only screen and (min-width: ${({ desktopMinWidth }: { desktopMinWidth: string }) =>
      desktopMinWidth}) {
      padding-left: 0;
      padding-right: 0;
    }
  }
  
  .promotion-accordion-overlay {
    z-index: 0 !important;
  }
`;

const StyledLoadingContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10rem 0;

  svg {
    width: 3rem;
    height: 3rem;
    color: #a12d6a;
  }
`;

const StyledLegacyMiniBanner = styled.div`
  width: 100%;
  max-width: 71.25rem;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const StyledPageIncludingNavigation = css`
  flex-direction: row;
  .cell-1 {
    width: 20%;
    padding-left: 0;
    padding-right: 2rem;
    padding-bottom: 0;
  }
  .cell-2 {
    width: 78%;
  }
`;

const StyledElementPageIncludingNavigation = styled.div`
  width: 100%;
  max-width: 71.25rem;
  display: flex;
  justify-content: space-between;
  height: 100%;
  padding-top: 2rem;
  flex-direction: column;

  .cell-1 {
    padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 2rem;
  }

  @media only screen and (min-width: ${({ tabletMinWidth }: { tabletMinWidth: string }) =>
      tabletMinWidth}) {
    ${StyledPageIncludingNavigation}
  }
`;

const StyledHintWrapper = styled.div`
  width: 100%;
  max-width: 71.25rem;
  display: flex;
  flex-direction: row;
  height: 100%;
  margin-bottom: 4rem;
  padding: 1rem 2rem;
  background-color: rgba(23, 165, 227, 0.7);
  color: white;
  font-size: 1.2rem;
  position: relative;

  div {
    &:first-of-type {
      padding-right: 2rem;
    }

    svg.icon-hint {
      width: 4.5rem;
      height: 4.5rem;
      opacity: 0.9;
    }

    svg.icon-arrow {
      width: 0.75rem;
      height: 0.75rem;
    }

    p {
      margin: 0;
      padding: 0 0 0.5rem 0;

      &:last-of-type {
        padding-bottom: 0;
      }

      a {
        text-decoration: underline;
        .icon-arrow {
          margin-right: 0.35rem;
        }
      }
    }
  }
`;

interface ErrorType {
  statusCode: number;
  statusMessage: string;
}

const getLocationFromHistory = (history?: History): Location | undefined => {
  if (history) {
    return history.location;
  }
  return undefined;
};

const PageContainer: FunctionComponent<{
  history?: History;
  testData?: any;
  configuration?: { useFallback?: boolean; fetchTimeout?: number };
}> = ({ history, testData, configuration }): JSX.Element => {
  const config = useConfig<FragmentConfig>();
  const { ArrowLargeLeft, Warning: IconError } = useIconSet();
  const location = getLocationFromHistory(history);
  const { revisionId = '', nlSheet = '' } = location
    ? (parse(history ? history.location.search : '') as { [key: string]: string })
    : { revisionId: '' };
  const pathname = getPath(location, config?.apiParams?.values?.apiDefaultParams?.localesToSwitch);
  const preferredLocale = userPreferredLocale(
    location?.pathname || '',
    config?.apiParams?.values?.apiDefaultParams?.locale,
    config?.apiParams?.values?.apiDefaultParams?.localesToSwitch,
  );
  const language = preferredLocale.substring(0, 2) as Languages;
  const apiCmsUrl = getCmsApiUrl(
    language,
    pathname,
    revisionId,
    config?.apiCmsUrl?.values?.url,
    config?.replace?.values?.page,
  );
  const apiCmsUrlFallback =
    getFallbackApiUrl(
      language,
      pathname,
      config?.apiCmsUrlFallback?.values?.url,
      config?.replace?.values?.page,
    ) || '';
  const useFallback =
    configuration?.useFallback !== undefined
      ? configuration.useFallback
      : config?.apiCmsUrlFallback?.enabled || false;
  const defaultData = testData || { field_pagegroup: 'missing' };
  const [cmsData, setCmsData] = useState<DataGlobal>(defaultData);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const fetchTimer = config?.fetchParams?.values?.timeout || 5000;
  const fetchTimerFallback =
    configuration?.fetchTimeout || config?.fetchParams?.values?.timeoutFallback || 5000;

  const callbackFetch = async (
    url: string,
    doFallbackFetch: boolean,
    timer = fetchTimerFallback,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> => {
    timeoutCapableFetch(
      parseInt(`${timer}`, 10),
      'Fetch Request timed out!',
      fetch(url)
        .then(
          // step 1: validate response
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (response: Response): Promise<any> => {
            if (!response.ok && !response.redirected) {
              const error: ErrorType = {
                statusCode: response.status,
                statusMessage: response.statusText,
              };
              throw new Error(JSON.stringify(error));
            }
            // Read the response as json.
            return response.json();
          },
        )
        .then(
          // step 2: read response as JSON
          (responseAsJson): JSON => {
            setCmsData(responseAsJson);
            setIsLoading(false);
            trackingProvider.page = responseAsJson.field_pagegroup || '';
            return responseAsJson;
          },
        )
        .catch((error: Error) => {
          const errorObject: ErrorType = JSON.parse(error.message);
          if (errorObject.statusCode >= 500 && doFallbackFetch) {
            callbackFetch(apiCmsUrlFallback, false);
            return false;
          }
          trackingProvider.page = 'error_404';
          setCmsData(FailureData);
          setIsLoading(false);
          return null;
        }),
    ).catch(() => {
      if (doFallbackFetch) {
        callbackFetch(apiCmsUrlFallback, false);
        return false;
      }
      trackingProvider.page = 'error_404';
      setCmsData(FailureData);
      setIsLoading(false);
      return null;
    });
  };

  useEffect(() => {
    callbackFetch(apiCmsUrl, useFallback, fetchTimer)
      .then(() => {
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, []);

  const itemID = cuid();

  const drupalUrl = `${config?.apiCmsUrlPrefix?.values?.url || ''}/${cmsData.langcode}/node/${
    cmsData.nid
  }/edit`;

  // JPWP-12259: client side rendering of esi to prevent layout bugs in the header
  const urlESI = config?.esiUrls?.values?.minibanner || null;

  const ElementPageContent = (): JSX.Element =>
    cmsData && cmsData?.paragraph ? (
      <>
        {urlESI && (
          <StyledLegacyMiniBanner>
            <Esi src={urlESI} />
          </StyledLegacyMiniBanner>
        )}
        <ComponentRecognizer content={cmsData.paragraph} key={itemID} />
        <div data-prerender="true" data-testid="cms-prerender-flag" />
      </>
    ) : (
      <></>
    );

  const ElementHint = (): JSX.Element => (
    <StyledHintWrapper data-testid="preview-hint">
      <div>
        <IconError className="icon-hint" color="white" />
      </div>
      <div>
        <p>Du befindest dich im Vorschaumodus.</p>
        <p>
          <a href={drupalUrl}>
            <ArrowLargeLeft className="icon-arrow" color="white" />
            zurück zu Drupal
          </a>
        </p>
      </div>
    </StyledHintWrapper>
  );

  const ElementLoader = (): JSX.Element => (
    <StyledLoadingContainer>
      <Loader />
    </StyledLoadingContainer>
  );

  const ElementPageIncludingNavigation = (): JSX.Element => (
    <StyledElementPageIncludingNavigation
      tabletMinWidth={config?.mediaBreakpoints?.values?.tabletMinWidth || ''}
    >
      <div className="cell cell-1">
        <CustomNavigation contentData={cmsData} />
      </div>
      <div className="cell cell-2">
        <ElementPageContent />
      </div>
    </StyledElementPageIncludingNavigation>
  );

  if (cmsData?.field_page_landmark) {
    trackingProvider.setLandmark({
      landmarkName: 'emma',
      template: cmsData.field_page_landmark,
    });
  }

  return (
    <>
      <Meta language={language} />
      <GlobalStyle desktopMinWidth={config?.mediaBreakpoints?.values?.desktopMinWidth || ''} />
      <StyledPageContainer className="page-container" data-testid="page-container">
        <HistoryContext.Provider value={{ history }}>
          {nlSheet === 'emma' && (
            <NewsletterSheet companyid={config?.global?.values?.FRAGMENT_COMPANYID as CompanyId} />
          )}
          {revisionId && <ElementHint />}
          {isLoading && !testData && <ElementLoader />}
          {cmsData &&
            (cmsData.menus && cmsData.menus.active && !cmsData.menus.shop.active ? (
              <ElementPageIncludingNavigation />
            ) : (
              <ElementPageContent />
            ))}
        </HistoryContext.Provider>
      </StyledPageContainer>
    </>
  );
};

export default PageContainer;
